import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_ICE_SERVERS,
  EMPTY_MEDIA,
  shouldInitiateOffer,
  type RtcMedia,
} from '../shared.js';
import { onRtcSignal, useApp } from '../store.js';

export type VoiceError = 'mic-denied' | 'mic-failed' | 'cam-failed' | 'connect-failed' | 'negotiate-failed';

export type VoiceChat = {
  inCall: boolean;
  joining: boolean;
  /** código do problema (voice.error.<código> no i18n) */
  error: VoiceError | null;
  micOn: boolean;
  camOn: boolean;
  localStream: MediaStream | null;
  remoteStreams: Record<string, MediaStream>;
  peerCount: number;
  join: () => Promise<void>;
  leave: () => void;
  toggleMic: () => void;
  toggleCam: () => Promise<void>;
};

/**
 * Voz e vídeo em malha (cada par conectado diretamente).
 *
 * O servidor só troca sinalização. Duas regras mantêm a malha coerente:
 *
 * 1. **Quem inicia a oferta**: o participante de menor id (`shouldInitiateOffer`).
 *    Sem isso, os dois lados ofereceriam ao mesmo tempo e as conexões brigariam.
 * 2. **Candidatos ICE que chegam antes da descrição remota** ficam numa fila e
 *    são aplicados depois — fora de ordem, `addIceCandidate` falharia.
 * 3. **Ofertas cruzadas** (os dois ligam a câmera ao mesmo tempo): quem puxa a conexão segue com
 *    a sua; o outro desfaz a dele, responde e oferece de novo quando a troca termina. Sem isso, as
 *    duas ofertas se desfaziam e ninguém recebia o vídeo do outro.
 */
export function useVoiceChat(): VoiceChat {
  const [inCall, setInCall] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<VoiceError | null>(null);
  // as preferências de Conferência decidem como a chamada começa
  const [micOn, setMicOn] = useState(() => useApp.getState().settings.joinWithMicOn);
  const [camOn, setCamOn] = useState(() => useApp.getState().settings.joinWithCameraOn);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({});

  const peersRef = useRef(new Map<string, RTCPeerConnection>());
  const pendingCandidatesRef = useRef(new Map<string, RTCIceCandidateInit[]>());
  const localStreamRef = useRef<MediaStream | null>(null);

  const peerMedia = useApp((state) => state.peerMedia);
  const users = useApp((state) => state.users);
  const inCallRef = useRef(false);
  /** fila por par: cada sinal espera o anterior (oferta antes dos candidatos) */
  const chainsRef = useRef(new Map<string, Promise<void>>());
  /** pares que ficaram devendo uma oferta (a mídia mudou no meio de outra troca) */
  const needsOfferRef = useRef(new Set<string>());
  /**
   * Muda ao sair da chamada e ao desmontar (sair da mesa): a permissão de microfone ou câmera que
   * chega depois disso não liga nada nem anuncia a chamada (na mesa seguinte, inclusive).
   */
  const callSeqRef = useRef(0);
  /** um pedido de microfone (entrar) ou de câmera em andamento: o segundo clique não pede outro */
  const askingMicRef = useRef(false);
  const askingCamRef = useRef(false);

  /** Roda `task` na fila do par, depois dos sinais que já estão nela. */
  const enqueue = useCallback((peerId: string, task: () => Promise<void>): void => {
    const previous = chainsRef.current.get(peerId) ?? Promise.resolve();
    chainsRef.current.set(peerId, previous.then(task));
  }, []);

  const sendOffer = useCallback(async (peerId: string, peer: RTCPeerConnection): Promise<void> => {
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    useApp.getState().sendSignal(peerId, { kind: 'offer', sdp: offer.sdp ?? '' });
  }, []);

  const publishMedia = useCallback((): void => {
    const stream = localStreamRef.current;
    const media: RtcMedia = {
      joined: inCallRef.current,
      audio: stream?.getAudioTracks().some((track) => track.enabled) ?? false,
      video: stream?.getVideoTracks().some((track) => track.enabled) ?? false,
      screen: false,
    };
    useApp.getState().sendMedia(media);
  }, []);

  const closePeer = useCallback((peerId: string): void => {
    const peer = peersRef.current.get(peerId);
    if (peer) {
      peer.onicecandidate = null;
      peer.ontrack = null;
      peer.onconnectionstatechange = null;
      peer.close();
    }
    peersRef.current.delete(peerId);
    pendingCandidatesRef.current.delete(peerId);
    needsOfferRef.current.delete(peerId);
    setRemoteStreams((current) => {
      if (!(peerId in current)) return current;
      const { [peerId]: _removido, ...resto } = current;
      return resto;
    });
  }, []);

  const drainCandidates = useCallback(async (peerId: string, peer: RTCPeerConnection): Promise<void> => {
    const queued = pendingCandidatesRef.current.get(peerId);
    if (!queued?.length) return;
    pendingCandidatesRef.current.delete(peerId);
    for (const candidate of queued) {
      try {
        await peer.addIceCandidate(candidate);
      } catch {
        // candidato inválido/expirado: seguir em frente é melhor que travar a chamada
      }
    }
  }, []);

  const createPeer = useCallback(
    (peerId: string, initiator: boolean): RTCPeerConnection => {
      const existing = peersRef.current.get(peerId);
      if (existing) return existing;

      const peer = new RTCPeerConnection({ iceServers: [...DEFAULT_ICE_SERVERS] });
      peersRef.current.set(peerId, peer);

      const stream = localStreamRef.current;
      if (stream) {
        for (const track of stream.getTracks()) peer.addTrack(track, stream);
      }

      peer.onicecandidate = (event) => {
        if (!event.candidate) return;
        // `toJSON()` do navegador tipa a linha do candidato como opcional; o
        // protocolo exige a string, então normalizamos aqui.
        const json = event.candidate.toJSON();
        const candidateLine = json.candidate ?? '';
        if (!candidateLine) return;
        useApp.getState().sendSignal(peerId, {
          kind: 'candidate',
          candidate: {
            candidate: candidateLine,
            sdpMid: json.sdpMid ?? null,
            sdpMLineIndex: json.sdpMLineIndex ?? null,
            ...(json.usernameFragment === undefined ? {} : { usernameFragment: json.usernameFragment }),
          },
        });
      };

      peer.ontrack = (event) => {
        const [incoming] = event.streams;
        if (!incoming) return;
        setRemoteStreams((current) => ({ ...current, [peerId]: incoming }));
      };

      peer.onconnectionstatechange = () => {
        if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
          closePeer(peerId);
        }
      };

      if (initiator) {
        void (async () => {
          const offer = await peer.createOffer();
          await peer.setLocalDescription(offer);
          useApp.getState().sendSignal(peerId, { kind: 'offer', sdp: offer.sdp ?? '' });
        })().catch(() => setError('connect-failed'));
      }

      return peer;
    },
    [closePeer],
  );

  const join = useCallback(async (): Promise<void> => {
    if (inCall || askingMicRef.current) return;
    askingMicRef.current = true;
    const seq = callSeqRef.current;
    setJoining(true);
    setError(null);
    try {
      // microfone escolhido em Configurações → Áudio e vídeo (se sumiu, o navegador usa outro)
      const micId = useApp.getState().settings.micDeviceId;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: micId } : true, video: false });
      if (seq !== callSeqRef.current) {
        // saiu da mesa enquanto o navegador perguntava: o microfone não fica ligado
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      localStreamRef.current = stream;
      setLocalStream(stream);
      // "entrar com o microfone ligado" (preferência de Conferência)
      const micPref = useApp.getState().settings.joinWithMicOn;
      for (const track of stream.getAudioTracks()) track.enabled = micPref;
      inCallRef.current = true;
      setInCall(true);
      setMicOn(micPref);
      setCamOn(false);
      // anuncia que entrou; quem já está na chamada e deve puxar a conexão o fará
      // (e nós puxamos a de quem já está, pelo efeito de membros abaixo)
      publishMedia();
    } catch (problem) {
      if (seq !== callSeqRef.current) return;
      const negado = problem instanceof Error && problem.name === 'NotAllowedError';
      setError(negado ? 'mic-denied' : 'mic-failed');
    } finally {
      askingMicRef.current = false;
      setJoining(false);
    }
  }, [inCall, publishMedia]);

  const leave = useCallback((): void => {
    callSeqRef.current += 1;
    for (const peerId of [...peersRef.current.keys()]) closePeer(peerId);
    const stream = localStreamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
    }
    localStreamRef.current = null;
    setLocalStream(null);
    setRemoteStreams({});
    inCallRef.current = false;
    chainsRef.current.clear();
    setInCall(false);
    setCamOn(false);
    setMicOn(true);
    useApp.getState().sendMedia(EMPTY_MEDIA);
  }, [closePeer]);

  const toggleMic = useCallback((): void => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    const next = !tracks.some((track) => track.enabled);
    for (const track of tracks) track.enabled = next;
    setMicOn(next);
    publishMedia();
  }, [publishMedia]);

  const toggleCam = useCallback(async (): Promise<void> => {
    const stream = localStreamRef.current;
    if (!stream) return;

    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length > 0 && videoTracks.some((track) => track.enabled)) {
      for (const track of videoTracks) {
        track.enabled = false;
        track.stop();
        stream.removeTrack(track);
      }
      for (const peer of peersRef.current.values()) {
        const sender = peer.getSenders().find((candidate) => candidate.track?.kind === 'video');
        if (sender) peer.removeTrack(sender);
      }
      setCamOn(false);
      publishMedia();
      return;
    }

    if (askingCamRef.current) return;
    askingCamRef.current = true;
    const seq = callSeqRef.current;
    try {
      const camId = useApp.getState().settings.cameraDeviceId;
      const camera = await navigator.mediaDevices.getUserMedia({ video: camId ? { deviceId: camId } : true });
      if (seq !== callSeqRef.current || localStreamRef.current !== stream) {
        // saiu da chamada (ou da mesa) enquanto o navegador perguntava: a câmera não fica ligada
        for (const track of camera.getTracks()) track.stop();
        return;
      }
      const [track] = camera.getVideoTracks();
      if (!track) return;
      stream.addTrack(track);
      for (const peer of peersRef.current.values()) peer.addTrack(track, stream);
      setCamOn(true);

      // ligar a câmera muda a mídia: é preciso renegociar com cada par, na fila dele; no meio de
      // outra troca, a oferta sai quando ela terminar
      for (const [peerId, peer] of peersRef.current) {
        enqueue(peerId, async () => {
          if (peersRef.current.get(peerId) !== peer) return;
          if (peer.signalingState !== 'stable') {
            needsOfferRef.current.add(peerId);
            return;
          }
          try {
            await sendOffer(peerId, peer);
          } catch {
            setError('negotiate-failed');
          }
        });
      }
      publishMedia();
    } catch {
      if (seq === callSeqRef.current) setError('cam-failed');
    } finally {
      askingCamRef.current = false;
    }
  }, [publishMedia, enqueue, sendOffer]);

  /** Sinalização que chega: em ordem, por par (só vale enquanto estou na chamada). */
  useEffect(
    () =>
      onRtcSignal((peerId, signal) => {
        if (!inCallRef.current) return;
        // só quem aparece na chamada conecta: sem isto, alguém fora dela receberia microfone e câmera
        // sem estar na lista (o `rtc.media` dele chega antes de qualquer sinal)
        if (!useApp.getState().peerMedia[peerId]?.joined) return;
        enqueue(peerId, async () => {
          if (!inCallRef.current) return;
          try {
            const peer = peersRef.current.get(peerId) ?? createPeer(peerId, false);
            if (signal.kind === 'offer') {
              if (peer.signalingState !== 'stable') {
                // ofertas cruzadas: quem puxa a conexão ignora a do outro (a resposta à sua vem)
                const me = useApp.getState().user?.id ?? '';
                if (shouldInitiateOffer(me, peerId)) return;
                // o outro desfaz a sua, responde e oferece de novo quando a troca terminar
                await peer.setLocalDescription({ type: 'rollback' });
                needsOfferRef.current.add(peerId);
              }
              await peer.setRemoteDescription({ type: 'offer', sdp: signal.sdp ?? '' });
              await drainCandidates(peerId, peer);
              const answer = await peer.createAnswer();
              await peer.setLocalDescription(answer);
              useApp.getState().sendSignal(peerId, { kind: 'answer', sdp: answer.sdp ?? '' });
            } else if (signal.kind === 'answer') {
              // resposta a uma oferta que desfizemos (ofertas cruzadas): não vale mais
              if (peer.signalingState !== 'have-local-offer') return;
              await peer.setRemoteDescription({ type: 'answer', sdp: signal.sdp ?? '' });
              await drainCandidates(peerId, peer);
            } else if (signal.kind === 'candidate' && signal.candidate) {
              if (peer.remoteDescription) {
                try {
                  await peer.addIceCandidate(signal.candidate);
                } catch {
                  // candidato de uma oferta ignorada (ofertas cruzadas): seguir em frente
                }
              } else {
                const queue = pendingCandidatesRef.current.get(peerId) ?? [];
                queue.push(signal.candidate);
                pendingCandidatesRef.current.set(peerId, queue);
              }
            }
            // a troca terminou: sai a oferta que ficou devendo
            if (peer.signalingState === 'stable' && needsOfferRef.current.delete(peerId)) await sendOffer(peerId, peer);
          } catch {
            setError('negotiate-failed');
          }
        });
      }),
    [createPeer, drainCandidates, enqueue, sendOffer],
  );

  /**
   * Quem está na conferência mudou: conecta com quem entrou (quem tem o menor id
   * puxa a conexão) e derruba quem saiu da chamada ou da sala.
   */
  useEffect(() => {
    if (!inCall) return;
    const { user } = useApp.getState();
    if (!user) return;
    const joined = new Set(users.filter((u) => u.id !== user.id && peerMedia[u.id]?.joined).map((u) => u.id));
    for (const id of joined) {
      if (!peersRef.current.has(id) && shouldInitiateOffer(user.id, id)) createPeer(id, true);
    }
    for (const peerId of [...peersRef.current.keys()]) {
      if (!joined.has(peerId)) {
        closePeer(peerId);
        chainsRef.current.delete(peerId);
      }
    }
  }, [users, peerMedia, inCall, createPeer, closePeer]);

  /**
   * A conexão com o servidor caiu e voltou (um `ready` novo). Sem outra janela minha na mesa, o
   * servidor apagou a minha mídia ao cair e os outros fecharam a conexão comigo: ainda na chamada,
   * fecho os pares velhos e anuncio de novo, e o efeito de membros reconecta com quem está.
   */
  const readySeq = useApp((state) => state.readySeq);
  const seenReadyRef = useRef(readySeq);
  useEffect(() => {
    if (seenReadyRef.current === readySeq) return;
    seenReadyRef.current = readySeq;
    if (!inCallRef.current) return;
    const { user, peerMedia: media } = useApp.getState();
    if (!user || media[user.id]?.joined) return;
    for (const peerId of peersRef.current.keys()) {
      closePeer(peerId);
      chainsRef.current.delete(peerId);
    }
    publishMedia();
  }, [readySeq, closePeer, publishMedia]);

  /** "Entrar com a câmera ligada" (Configurações → Áudio e vídeo): liga logo depois de entrar. */
  const autoCamRef = useRef(false);
  useEffect(() => {
    if (!inCall) {
      autoCamRef.current = false;
      return;
    }
    if (autoCamRef.current) return;
    autoCamRef.current = true;
    if (useApp.getState().settings.joinWithCameraOn && !camOn) void toggleCam();
  }, [inCall, camOn, toggleCam]);

  /** Encerra tudo ao desmontar (sair da sala). */
  useEffect(() => {
    return () => {
      callSeqRef.current += 1;
      for (const peer of peersRef.current.values()) peer.close();
      peersRef.current.clear();
      pendingCandidatesRef.current.clear();
      for (const track of localStreamRef.current?.getTracks() ?? []) track.stop();
      localStreamRef.current = null;
    };
  }, []);

  return {
    inCall,
    joining,
    error,
    micOn,
    camOn,
    localStream,
    remoteStreams,
    peerCount: Object.keys(remoteStreams).length,
    join,
    leave,
    toggleMic,
    toggleCam,
  };
}
